import { React, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Container,
  Link,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Autocomplete} from '@mui/material';
import { Label, Input } from 'reactstrap';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Page from '../../../src/components/Page';
import { styled } from '@mui/system';
import theme from '../../theme';
import States from "../../mixins/States";
import LegalLinks, { LOGIN_PATH } from "../../components/LegalLinks";
import { useRegisterUser } from "../../hooks/useAuth";

const MyPage = styled(Page)({
  backgroundColor: theme.palette.background.default,
  height: '100%',
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3)
})

const RegisterView = () => {
    const navigate = useNavigate();
    const registerUser = useRegisterUser();

    const [showPassword1, setShowPassword1] = useState(false);
    const handleClickShowPassword1 = () => setShowPassword1(!showPassword1);
    const handleMouseDownPassword1 = () => setShowPassword1(!showPassword1);
    const [showPassword2, setShowPassword2] = useState(false);
    const handleClickShowPassword2 = () => setShowPassword2(!showPassword2);
    const handleMouseDownPassword2 = () => setShowPassword2(!showPassword2);

    const [rulerSN, setRulerSN] = useState('');

    const [yesRuler, setYesRuler] = useState(false);
    const [isProviderNeeded, setIsProviderNeeded] = useState(false);
    const [isExternal, setIsExternal] = useState(false);
    const [isValidationOnly, setIsValidationOnly] = useState(false);

    function YesRuler(){
        setYesRuler(true);
        setIsProviderNeeded(false);
        setIsExternal(false);
        setIsValidationOnly(false);
    }
    function NoRuler(){
        setYesRuler(false);
        setIsProviderNeeded(false);
        setIsExternal(false);
        setIsValidationOnly(false);
        setRulerSN('');
    }

    return (
        <MyPage title="Register" className='0' style={{ marginBottom:15}}>
            <Box className="1" display="flex" flexDirection="column" height="100%" justifyContent="center" style={{height:'100%'}}>
                <Container maxWidth="sm" className='2' style={{height:'100%'}}>
                    <Formik
                        initialValues={{
                            UserName: '',
                            UserEmail: '',
                            FirstName: '',
                            LastName: '',
                            Password: '',
                            Confirm: '',

                            CompanyName: '',
                            Street: '',
                            City: '',
                            State: '',
                            Zip: '',
                            Country: 'United States',
                            CompanyEmail: '',
                            AccountingEmail: '',
                            PhoneNumber: '',

                            IsProviderNeeded: false,
                            IsExternal: false,
                            IsValidationOnly: false,
                            RulerSN: null,

                        }}
                        validationSchema={
                            Yup.object().shape({
                                UserName: Yup.string().max(255).required('Username is required'),
                                FirstName: Yup.string().max(255).required('First name is required'),
                                LastName: Yup.string().max(255).required('Last name is required'),
                                Password: Yup.string().max(255).required('Password is required'),
                                Confirm: Yup.string().max(255).required('Confirm password').oneOf([Yup.ref('Password'), null], 'Passwords must match'),

                                CompanyName: Yup.string().max(255).required('Company name required'),
                                Street: Yup.string().max(255).required('Street required'),
                                City: Yup.string().max(255).required('City required'),
                                State: Yup.string().max(255).required('State required'),
                                Zip: Yup.string().max(10).required('Zip code required'),
                                CompanyEmail: Yup.string().email('Enter a valid email').max(225).required('Email Required'),
                                PhoneNumber: Yup.string().max(225).required('Phone Required'),

                                IsProviderNeeded: Yup.boolean(),
                            })
                        }
                        onSubmit={(values) => {
                            const trimmedRuler = String(rulerSN || '').trim();
                            const payload = {
                                UserName: values.UserName,
                                UserEmail: values.CompanyEmail,
                                FirstName: values.FirstName,
                                LastName: values.LastName,
                                Password: values.Password,
                                ConfirmPassword: values.Confirm,
                                CompanyName: values.CompanyName,
                                Street: values.Street,
                                City: values.City,
                                State: values.State,
                                Zip: values.Zip,
                                Country: 'United States',
                                CompanyEmail: values.CompanyEmail,
                                AccountingEmail: values.AccountingEmail || '',
                                PhoneNumber: values.PhoneNumber,
                                IsProviderNeeded: isProviderNeeded,
                                IsExternal: isExternal,
                                IsValidationOnly: isValidationOnly,
                                // Only send a SN when Yes is selected and digits were entered.
                                RulerSN: yesRuler && trimmedRuler ? `EvanG3-${trimmedRuler}` : null,
                            };
                            registerUser.mutate({ payload, navigate })
                        }}
                    >
                        {({
                            errors,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            setFieldValue,
                            touched,
                            values
                        }) => (
                        <form onSubmit={handleSubmit} style={{height:'100%'}}>
                            <Box style={{border:'solid black 2px', borderRadius:'10px', padding: 15, boxShadow: '5px 5px 5px 3px rgba(168,168,168,0.45)'}}>
                            <Box mb={3}>
                                <Typography color="textPrimary" variant="h3" style={{borderBottom: 'solid grey 2px'}}>
                                    Create New Account
                                </Typography>
                            </Box>
                            <Box style={{display:'flex', flexDirection:'column'}}>
                            <Box style={{padding:5, borderBottom: 'solid grey 2px'}}>
                                        <h3 style={{borderBottom:'dashed grey 2px'}}>Company Information</h3>
                                    <Box style={{display:'flex', flexDirection:"row"}}>
                                        <TextField
                                            style={{margin:5}}
                                            error={Boolean(touched.CompanyName && errors.CompanyName)}
                                            fullWidth
                                            helperText={touched.CompanyName && errors.CompanyName}
                                            label="Company Name"
                                            margin="normal"
                                            name="CompanyName"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.CompanyName}
                                            variant="outlined"
                                        />
                                        <TextField
                                            style={{margin:5}}
                                            error={Boolean(touched.PhoneNumber && errors.PhoneNumber)}
                                            fullWidth
                                            helperText={touched.PhoneNumber && errors.PhoneNumber}
                                            label="Company Phone"
                                            margin="normal"
                                            name="PhoneNumber"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            variant="outlined"
                                            value={values.PhoneNumber}
                                        />
                                    </Box>
                                    <Box style={{display:'flex', flexDirection:"row"}}>
                                        <TextField
                                            style={{margin:5}}
                                            error={Boolean(touched.CompanyEmail && errors.CompanyEmail)}
                                            fullWidth
                                            helperText={touched.CompanyEmail && errors.CompanyEmail}
                                            label="Company Primary Email"
                                            margin="normal"
                                            name="CompanyEmail"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            variant="outlined"
                                            value={values.CompanyEmail}
                                        />
                                        <TextField
                                            style={{margin:5}}
                                            error={Boolean(touched.AccountingEmail && errors.AccountingEmail)}
                                            fullWidth
                                            helperText={touched.AccountingEmail && errors.AccountingEmail}
                                            label="Accounting Email (Optional)"
                                            margin="normal"
                                            name="AccountingEmail"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            variant="outlined"
                                            value={values.AccountingEmail}
                                        />
                                    </Box>
                                    <Box style={{display:'flex', flexDirection:"row"}}>
                                        <TextField
                                            style={{margin:5}}
                                            error={Boolean(touched.Street && errors.Street)}
                                            fullWidth
                                            helperText={touched.Street && errors.Street}
                                            label="Street"
                                            margin="normal"
                                            name="Street"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.Street}
                                            variant="outlined"
                                        />
                                        <TextField
                                            style={{margin:5}}
                                            error={Boolean(touched.City && errors.City)}
                                            fullWidth
                                            helperText={touched.City && errors.City}
                                            label="City"
                                            margin="normal"
                                            name="City"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.City}
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Box style={{display:'flex', flexDirection:"row"}}>
                                        <Autocomplete
                                            fullWidth
                                            options={States}
                                            autoHighlight
                                            name="State"
                                            value={States.find((s) => s.name === values.State) || null}
                                            onChange={(e, value) => setFieldValue("State", value?.name || "")}
                                            isOptionEqualToValue={(option, value) => option?.name === value?.name}
                                            getOptionLabel={(option) => option?.name ?? ''}
                                            style={{marginLeft:5, marginRight:5, marginTop:5}}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    name="State"
                                                    error={Boolean(touched.State && errors.State)}
                                                    helperText={touched.State && errors.State}
                                                    label="State"
                                                    onBlur={handleBlur}
                                                />
                                            )}
                                        />
                                        <TextField
                                            style={{margin:5}}
                                            error={Boolean(touched.Zip && errors.Zip)}
                                            fullWidth
                                            helperText={touched.Zip && errors.Zip}
                                            label="Zip"
                                            margin="normal"
                                            name="Zip"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            variant="outlined"
                                            value={values.Zip}
                                        />
                                    </Box>
                                </Box>
                                <Box style={{padding:5, borderBottom: 'solid grey 2px'}}>
                                    <h3 style={{borderBottom:'grey dashed 2px'}}>Admin Information</h3>
                                    <Box style={{display:'flex', flexDirection:"row"}}>
                                        <TextField
                                            style={{margin:5}}
                                            error={Boolean(touched.FirstName && errors.FirstName)}
                                            fullWidth
                                            helperText={touched.FirstName && errors.FirstName}
                                            label="Admin First Name"
                                            margin="normal"
                                            name="FirstName"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.FirstName}
                                            variant="outlined"
                                        />
                                        <TextField
                                            style={{margin:5}}
                                            error={Boolean(touched.LastName && errors.LastName)}
                                            fullWidth
                                            helperText={touched.LastName && errors.LastName}
                                            label="Admin Last Name"
                                            margin="normal"
                                            name="LastName"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.LastName}
                                            variant="outlined"
                                        />
                                    </Box>
                                    <h3 style={{borderBottom:'grey dashed 2px'}}>Login Information</h3>
                                    <Box style={{display:'flex', flexDirection:"row"}}>
                                        <TextField
                                            style={{margin:5}}
                                            error={Boolean(touched.UserName && errors.UserName)}
                                            fullWidth
                                            helperText={touched.UserName && errors.UserName}
                                            label="Username"
                                            margin="normal"
                                            name="UserName"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.UserName}
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Box style={{display:'flex', flexDirection:"row"}}>
                                        <TextField
                                            style={{margin:5}}
                                            error={Boolean(touched.Password && errors.Password)}
                                            fullWidth
                                            helperText={touched.Password && errors.Password}
                                            label="Password"
                                            margin="normal"
                                            name="Password"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            type={showPassword1 ? "text" : "password"}
                                            value={values.Password}
                                            variant="outlined"
                                            InputProps={{ // <-- This is where the toggle button is added.
                                                endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword1}
                                                    onMouseDown={handleMouseDownPassword1}
                                                    >
                                                    {showPassword1 ? <Visibility /> : <VisibilityOff />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )}}
                                        />
                                        <TextField
                                            style={{margin:5}}
                                            error={Boolean(touched.Confirm && errors.Confirm)}
                                            fullWidth
                                            helperText={touched.Confirm && errors.Confirm}
                                            label="Confirm Password"
                                            margin="normal"
                                            name="Confirm"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            type={showPassword2 ? "text" : "password"}
                                            value={values.Confirm}
                                            variant="outlined"
                                            InputProps={{ // <-- This is where the toggle button is added.
                                                endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword2}
                                                    onMouseDown={handleMouseDownPassword2}
                                                    >
                                                    {showPassword2 ? <Visibility /> : <VisibilityOff />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )}}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <Box style={{margin:10}}>
                                <Label check>
                                    <b>Purchased an EvanG3 E-Ruler?</b>
                                </Label>
                                <Label check>
                                    <Input type="switch" role="switch" checked={yesRuler} onChange={()=> YesRuler()}/>
                                    Yes
                                </Label>
                                <Label check>
                                    <Input type="switch" role="switch" checked={!yesRuler} onChange={()=> NoRuler()}/>
                                    No
                                </Label>

                                { yesRuler == true &&
                                    <div>
                                        <br/>
                                        <span>Please enter last 6 digits of serial Number: ex. XXXXXX</span>
                                        <TextField
                                            label="Ruler Serial Number"
                                            margin="normal"
                                            value={rulerSN}
                                            onChange={(event) => setRulerSN(event.target.value)}
                                        />
                                    </div>
                                }
                            </Box>
                            <Box my={2}>
                                <Button
                                    color="primary"
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                >
                                    Sign up now
                                </Button>
                            </Box>
                            <Typography color="textSecondary" variant="body1">
                                Have an account?
                                {' '}
                                <Link component={RouterLink} to={LOGIN_PATH} variant="h6">
                                    Sign in
                                </Link>
                                <br/>
                                For any questions or major issues while signing up please reach out to Botmark or Layout Specialties for assistance.
                            </Typography>
                            <Box mt={2}>
                                <LegalLinks/>
                            </Box>
                            </Box>
                        </form>
                        )}
                    </Formik>
                </Container>
            </Box>
        </MyPage>
    );
};

export default RegisterView;